/**
 * Preview visibility for unconfirmed suggestion-mode edits.
 *
 * Situation-report (and other) suggestions persist `insertion` marks on text
 * leaves only. y-prosemirror 1.3.x does not round-trip marks on block nodes, so
 * a suggested heading, list, or table arrives as a real block whose text is
 * marked `insertion`. Preview already drops that text; these helpers also drop
 * the empty shell and its classification prefix when nothing accepted remains.
 *
 * An empty structural node (a blank paragraph, an empty table cell) is neither
 * accepted content nor a pending insertion, so a user-authored empty block
 * still renders, and an empty cell does not keep an otherwise unconfirmed
 * table on screen.
 */

const STRUCTURAL_TYPES = new Set([
    'doc',
    'paragraph',
    'heading',
    'blockquote',
    'bullet_list',
    'ordered_list',
    'list_item',
    'table',
    'table_row',
    'table_cell',
    'table_header',
]);

export function isPendingInsertion(node) {
    const marks = node?.marks;
    return Array.isArray(marks) && marks.some((mark) => mark?.type === 'insertion');
}

/**
 * @param {object|null|undefined} node ProseMirror JSON node
 * @returns {{accepted: boolean, pending: boolean}}
 */
export function previewSuggestionPresence(node) {
    if (!node || typeof node !== 'object') {
        return {accepted: false, pending: false};
    }
    if (isPendingInsertion(node)) {
        return {accepted: false, pending: true};
    }
    if (node.type === 'text') {
        return {
            accepted: typeof node.text === 'string' && node.text.length > 0,
            pending: false,
        };
    }
    if (!Array.isArray(node.content) || node.content.length === 0) {
        if (STRUCTURAL_TYPES.has(node.type)) {
            return {accepted: false, pending: false};
        }
        return {accepted: true, pending: false};
    }

    let accepted = false;
    let pending = false;
    for (const child of node.content) {
        const presence = previewSuggestionPresence(child);
        accepted = accepted || presence.accepted;
        pending = pending || presence.pending;
    }
    return {accepted, pending};
}

/** True when the node is only an unconfirmed insertion and has no accepted content. */
export function shouldHideUnconfirmedPreviewNode(node) {
    const {accepted, pending} = previewSuggestionPresence(node);
    return pending && !accepted;
}
